import React, { useEffect } from 'react'
import { FlatList } from 'react-native'
import { useNavigation } from '@react-navigation/native'
import { connect } from 'react-redux'
import styled from 'styled-components/native'
import { SafeAreaView } from 'react-native-safe-area-context'

import PatientActions from 'APP/Redux/PatientRedux'

import { useDevTools } from 'APP/Lib/DevTools'

// Services
import I18n from 'APP/I18n'

// Components
import Button from 'APP/Converse/Button'
import { ResponsiveScrollView, ResponsiveView } from 'APP/Converse/Layout'
import Typography from 'APP/Converse/Typography'
import LogoHeader from 'APP/Components/LogoHeader'

// Config
import { Images } from 'APP/Themes'

const ScreenContainer = styled(SafeAreaView).attrs({
  edges: ['bottom', 'left', 'right'],
})`
  flex: 1;
`

const ContentContainer = styled(ResponsiveView)`
  gap: ${({ theme }) => theme.metrics.baseMargin * 2}px;
  padding: ${({ theme }) => theme.metrics.baseMargin}px;
`

const ContentFooterContainer = styled.View`
  gap: ${({ theme }) => theme.metrics.baseMargin}px;
`

const OrganizationCard = styled.View`
  margin-vertical: ${({ theme }) => theme.metrics.baseMargin / 2}px;
  background-color: ${({ theme }) => theme.colors.elementsBg};
  padding: ${({ theme }) => theme.metrics.baseMargin}px;
  border-radius: ${({ theme }) => theme.metrics.borderRadius.small};
  ${({ theme }) => theme.shadows.normal}
`

function ClaimAccessSuccessScreen({
  displayNames,
  isInChallengedQueue,
  nextChallenge,
  isOnboarding,
  onboardingDeferredEnrolmentComplete,
}) {
  const navigation = useNavigation()
  const { setToolName, setToolFunction } = useDevTools()

  useEffect(() => {
    setToolName('Next')
    setToolFunction(() => () => {
      onCTAPress()
    })
    return () => {
      setToolFunction(null)
      setToolName(null)
    }
  }, [])

  const onCTAPress = () => {
    if (isInChallengedQueue) {
      nextChallenge()
    } else if (isOnboarding && !isInChallengedQueue) {
      onboardingDeferredEnrolmentComplete()
    } else {
      navigation.reset({ index: 0, routes: [{ name: 'tabbar' }] })
    }
  }

  const renderOrganization = (displayName) => {
    return (
      <OrganizationCard>
        <Typography variant="h4">{displayName[I18n.locale]}</Typography>
      </OrganizationCard>
    )
  }

  const CTAButtonTitle = I18n.t(
    isInChallengedQueue || isOnboarding
      ? 'ClaimAccessSuccessScreen.nextBtn'
      : 'ClaimAccessSuccessScreen.finishBtn'
  )

  return (
    <ScreenContainer>
      <ResponsiveScrollView alwaysBounceVertical={false} keyboardShouldPersistTaps="always">
        <ContentContainer>
          <LogoHeader
            source={Images.logoBlue}
            title={I18n.t('ClaimAccessSuccessScreen.title')}
            subtitle={I18n.t('ClaimAccessSuccessScreen.subtitle')}
            icon={{ src: 'lock-open', variant: 'materialIcons', colour: 'accent' }}
          />

          <FlatList
            data={displayNames}
            keyExtractor={(item, index) => `${index}-${item.en}`}
            renderItem={({ item }) => renderOrganization(item)}
          />
          <ContentFooterContainer>
            <Button
              variant="primary"
              widthVariant="full"
              title={CTAButtonTitle}
              analyticsName={'ClaimAccessSuccessScreen.next'}
              onPress={onCTAPress}
              testID="CLAIM_ACCESS_SUCCESS_CTA_BTN"
            />
          </ContentFooterContainer>
        </ContentContainer>
      </ResponsiveScrollView>
    </ScreenContainer>
  )
}

const mapStateToProps = (state, props) => {
  const { challengedInvitationQueue, isOnboarding } = state.patient

  return {
    // Note: displayNames are passed through route params from both flows (enrolWithEnrolmentCodes & enrolWithOrganizationId)
    ...props.route?.params,
    isInChallengedQueue: (challengedInvitationQueue ?? []).length > 0,
    isOnboarding,
  }
}

const mapDispatchToProps = (dispatch) => ({
  nextChallenge: () => dispatch(PatientActions.challengedInvitationQueueNext()),
  onboardingDeferredEnrolmentComplete: () =>
    dispatch(PatientActions.onboardingDeferredEnrolmentComplete()),
})

export default connect(mapStateToProps, mapDispatchToProps)(ClaimAccessSuccessScreen)
